<!-- One participant (or myself) in the call: video, or avatar when the camera
     is off, plus name / muted / screen badges and the link status overlay.
     The <video> stays mounted whatever is drawn over it, so the audio never
     stutters when the camera toggles. -->
<template>
  <div class="call-tile"
       :class="{ 'call-tile--stage': stage, 'call-tile--local': tile.local, 'call-tile--clickable': clickable }"
       @click="clickable && $emit('select')">
    <video-tile v-if="tile.stream" :stream="tile.stream" :muted="tile.local" :tick="tile.tick"
                :mirror="tile.local && !tile.sharing" :fit="fit" :sink-id="sinkId" />

    <div v-if="!showVideo" class="call-tile__avatar">
      <vs-avatar :src="tile.avatar" :text="tile.name || '?'" :size="stage ? '96px' : '56px'" />
    </div>

    <div v-if="status" class="call-tile__status">
      <span v-if="status.spin" class="call-tile__spinner"></span>
      <feather-icon v-else icon="WifiOffIcon" svgClasses="w-6 h-6" />
      <span>{{ status.text }}</span>
    </div>

    <div class="call-tile__label">
      <feather-icon v-if="!tile.mic" icon="MicOffIcon" svgClasses="w-3 h-3" class="call-tile__muted" />
      <span class="call-tile__name">{{ tile.name }}</span>
      <span v-if="tile.sharing" class="call-tile__chip">screen</span>
      <feather-icon v-if="tile.away && !status" icon="WifiOffIcon" svgClasses="w-3 h-3" class="call-tile__away" title="Signaling lost — reconnecting" />
    </div>
  </div>
</template>

<script>
import VideoTile from './VideoTile.vue'

export default {
  components: { VideoTile },
  props: {
    tile: { type: Object, required: true },
    stage: { type: Boolean, default: false },
    fit: { type: String, default: 'cover' },
    clickable: { type: Boolean, default: false },
    sinkId: { type: String, default: '' }
  },
  computed: {
    showVideo () {
      return !!this.tile.stream && (this.tile.sharing || this.tile.cam) && (this.tile.local || this.tile.connected)
    },
    status () {
      const t = this.tile
      if (t.local) return null
      if (t.link === 'lost') return { text: 'Connection lost', spin: false }
      if (t.link === 'unstable') return { text: 'Reconnecting…', spin: true }
      if (!t.connected) return { text: 'Connecting…', spin: true }
      return null
    }
  }
}
</script>

<style scoped>
.call-tile {
  position: relative;
  background: #1a1a2e;
  border-radius: 12px;
  overflow: hidden;
  min-height: 0;
  min-width: 0;
}
.call-tile--clickable { cursor: pointer; }
.call-tile--clickable:hover { outline: 2px solid rgba(255,255,255,.25); outline-offset: -2px; }
.call-tile >>> .meeting-video { position: absolute; inset: 0; }

.call-tile__avatar {
  position: absolute; inset: 0; z-index: 2;
  display: flex; align-items: center; justify-content: center;
  background: radial-gradient(circle at 50% 40%, #26264a 0%, #15152a 70%);
}
.call-tile__status {
  position: absolute; inset: 0; z-index: 3;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
  color: rgba(255,255,255,.85); font-size: 12px;
  background: rgba(10,10,22,.6);
  backdrop-filter: blur(2px);
}
.call-tile__spinner {
  width: 22px; height: 22px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.25); border-top-color: #fff;
  animation: call-spin .9s linear infinite;
}
@keyframes call-spin { to { transform: rotate(360deg); } }

.call-tile__label {
  position: absolute; left: 8px; bottom: 8px; z-index: 4;
  display: inline-flex; align-items: center; gap: 6px;
  max-width: calc(100% - 16px);
  background: rgba(0,0,0,.55); color: #fff;
  font-size: 12px; line-height: 1; padding: 5px 9px; border-radius: 999px;
}
.call-tile__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.call-tile__muted { color: #ea5455; flex-shrink: 0; }
.call-tile__away { color: #ff9f43; flex-shrink: 0; }
.call-tile__chip {
  background: #7367f0; color: #fff; font-size: 10px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .4px; padding: 2px 6px; border-radius: 999px;
}
.call-tile--stage .call-tile__label { font-size: 13px; left: 12px; bottom: 12px; }
</style>
